import { useCallback } from "react";

import { useRewriteAiInterviewQuestionMutation } from "../../graphql";
import { useAiInterviewerContext } from "./AiInterviewerContext";
import { useAiInterviewerFormContext } from "./useAiInterviewerFormContext";

const useRewriteQuestion = (
  index: number
): {
  rewriteQuestion: (steeringInstructions?: string) => Promise<void>;
  loading: boolean;
} => {
  const aiInterviewer = useAiInterviewerContext();
  const { setValue, getValues } = useAiInterviewerFormContext();

  const [rewriteQuestionMutation, { loading }] =
    useRewriteAiInterviewQuestionMutation();

  const rewriteQuestion = useCallback(
    async (steeringInstructions?: string) => {
      const trimmedInstructions = steeringInstructions?.trim();
      const formValues = getValues();
      const variables = {
        aiInterviewerId: aiInterviewer.id,
        aiInterviewerName: formValues.name,
        questions: formValues.questions.map(
          ({ sortOrder, __typename, originalQuestionId, ...rest }) => ({
            ...rest,
          })
        ),
        positionInfo: formValues.positionInfo,
        introduction: formValues.introduction,
        closing: formValues.closing,
        jobDescription: formValues.jobDescription,
        steeringInstructions: trimmedInstructions || undefined,
        targetQuestionPosition: index,
      };

      const result = await rewriteQuestionMutation({ variables });
      const updated =
        result.data?.rewriteAiInterviewQuestion?.aiInterviewQuestion;
      if (updated) {
        (
          [
            "question",
            "type",
            "followups",
            "rubric",
            "qualificationCriteria",
            "summaryCriteria",
          ] as const
        ).forEach((key) => {
          const fieldPath = `questions.${index}.${key}` as const;
          if (key in updated) {
            setValue(fieldPath, updated[key], { shouldDirty: true });
          }
        });
      }
    },
    [aiInterviewer.id, getValues, index, rewriteQuestionMutation, setValue]
  );

  return { rewriteQuestion, loading };
};

export default useRewriteQuestion;
